import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Megaphone } from "lucide-react";

export default function News() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="news-page">
      <div className="max-w-2xl">
        <div className="overline">Initiatives</div>
        <h1 className="mt-5 font-heading text-4xl sm:text-5xl leading-tight">AzSAM Initiatives</h1>
        <p className="mt-5 text-lg text-[color:var(--azsam-ink-soft)] leading-relaxed">
          AzSAM is committed to advancing addiction medicine both within the healthcare community
          and throughout Arizona. Through legislative advocacy, public education, professional
          training, community partnerships, and the promotion of evidence-based best practices, our
          members work to improve prevention, treatment, recovery, and public understanding of
          substance use disorders. The activities below highlight the many ways AzSAM is engaged in
          serving patients, supporting professionals, informing policy, and strengthening
          communities across the state.
        </p>
      </div>

      <div className="mt-12 p-8 lg:p-10 rounded-2xl bg-[color:var(--azsam-cream)] border border-[color:var(--azsam-stone)]" data-testid="news-project-callout">
        <div className="flex items-start gap-5">
          <div className="w-11 h-11 rounded-lg bg-[color:var(--azsam-clay)]/10 text-[color:var(--azsam-clay)] flex items-center justify-center flex-shrink-0">
            <Megaphone size={22} strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="overline">Member spotlight</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl leading-snug">
              Want to tell us about a project you're leading?
            </h2>
            <p className="mt-4 text-[color:var(--azsam-ink-soft)] leading-relaxed">
              AzSAM is always looking to highlight and support the outstanding work being done by
              our members across Arizona. Whether you are involved in advocacy, research, education,
              community outreach, policy development, overdose prevention, recovery initiatives, or
              innovative clinical programs, we want to hear about it. AzSAM may be able to help
              amplify your efforts through our network, connect you with collaborators, provide
              subject matter expertise, and help champion your initiative across the finish line.
            </p>
            <p className="mt-4 text-[color:var(--azsam-ink)] font-medium">
              Want to tell us about your project and get it highlighted?{" "}
              <Link to="/projects/submit" className="text-[color:var(--azsam-clay)] underline underline-offset-4 hover:text-[color:var(--azsam-clay-hover)]" data-testid="news-project-cta">
                Click here
              </Link>.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-8" data-testid="news-grid">
        {items.map((n) => (
          <Link to={`/news/${n.id}`} key={n.id} className="azsam-card overflow-hidden flex flex-col" data-testid={`news-${n.id}`}>
            {n.image_url && (
              <div className="h-52 overflow-hidden">
                <img src={n.image_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-7 flex flex-col flex-1">
              <div className="text-xs text-[color:var(--azsam-ink-soft)]">{formatDate(n.published)} · {n.author}</div>
              <h3 className="mt-3 font-heading text-2xl leading-snug">{n.title}</h3>
              <p className="mt-3 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed line-clamp-3">{n.excerpt}</p>
              <span className="mt-5 text-sm font-semibold text-[color:var(--azsam-clay)]">Read more →</span>
            </div>
          </Link>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-16 text-[color:var(--azsam-ink-soft)]" data-testid="news-empty">No articles yet.</div>
      )}
    </div>
  );
}

function formatDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
